// Meta Pixel loader — loaded ONLY after the user has given consent.
// Public Pixel ID is read from the backend (/api/config/public) so that we never
// hardcode it in the bundle.

import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

let pixelLoaded = false;
let cachedPixelId = null;
// Last `external_id` applied as Advanced Matching via fbq('init', PIXEL, {...}).
// Tracked here so we don't re-init the Pixel on every track() call.
let currentExternalId = null;

/**
 * Decode the `sub` claim from the user JWT stored in localStorage/sessionStorage.
 * The JWT is opaque — we only read it for the user_id we put inside `sub` at
 * sign time. No signature verification is done client-side (that's the server's
 * job on each authenticated request).
 *
 * Returns the user_id string, or null if no token / parsing fails.
 */
export function getCurrentExternalId() {
  if (typeof window === "undefined") return null;
  try {
    const token =
      window.localStorage.getItem("np_user_token") ||
      window.sessionStorage.getItem("np_user_token");
    if (!token) return null;
    const payload = token.split(".")[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
    const decoded = JSON.parse(json);
    return decoded?.sub || null;
  } catch {
    return null;
  }
}

async function getPixelId() {
  if (cachedPixelId !== null) return cachedPixelId;
  try {
    const { data } = await axios.get(`${API}/config/public`);
    cachedPixelId = data?.meta_pixel_id || "";
  } catch {
    cachedPixelId = "";
  }
  return cachedPixelId;
}

function injectPixelScript() {
  if (typeof window === "undefined") return;
  if (window.fbq) return;
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
}

function getQueryParam(name) {
  if (typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

/**
 * Extract `fbclid` preserving the ORIGINAL encoding from the URL.
 * Meta's spec is strict: do NOT lowercase, truncate or URL-decode the fbclid
 * before putting it into the `_fbc` cookie / fbc parameter. URLSearchParams
 * auto-decodes ('+' → ' ', '%XX' → char), so we have to parse manually.
 */
function getRawFbclidFromUrl() {
  if (typeof window === "undefined") return null;
  const search = window.location.search || "";
  if (!search) return null;
  // Strip leading '?' and split on '&' — preserving the original character set.
  const parts = search.replace(/^\?/, "").split("&");
  for (const p of parts) {
    const eq = p.indexOf("=");
    if (eq === -1) continue;
    const k = p.slice(0, eq);
    if (k === "fbclid") return p.slice(eq + 1);  // ← raw, never decoded
  }
  return null;
}

/**
 * Generates an 8-char alphanumeric appendix (mix of upper, lower, digits).
 * Required by Meta's updated `_fbp`/`_fbc` cookie format:
 *   `fb.${subdomain_index}.${creation_time}.${value}.${appendix}`
 * The appendix prevents cross-session bleed and is part of the canonical
 * format Meta documents (e.g. `fb.1.1596403881668.1116446470.ABcDEFGh`).
 */
function generateAppendix() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let out = "";
  for (let i = 0; i < 8; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

/**
 * Returns `_fbp` from cookie, or generates one in the format Meta documents:
 *   `fb.1.<ts_ms>.<10-digit-random>.<8-char-appendix>`
 * The browser-side Pixel will normally read the cookie we set; if both
 * client + server use the same `_fbp` value, Meta dedupe + match quality
 * stays high.
 */
function ensureFbp() {
  let fbp = getCookie("_fbp");
  if (fbp) return fbp;
  const ts = Date.now();
  const rand = Math.floor(1000000000 + Math.random() * 9000000000);
  fbp = `fb.1.${ts}.${rand}.${generateAppendix()}`;
  try { document.cookie = `_fbp=${fbp}; path=/; max-age=${60 * 60 * 24 * 90}; SameSite=Lax`; } catch { /* noop */ }
  return fbp;
}

/**
 * Returns `_fbc` from cookie, or constructs it from the `fbclid` URL param.
 * Format (per Meta 2024 spec):
 *   `fb.1.<ts_ms>.<fbclid>.<8-char-appendix>`
 *
 * The fbclid MUST be the RAW URL-encoded value from the query string — Meta
 * rejects events where the fbclid was lowercased, truncated, or URL-decoded.
 * That's why we use `getRawFbclidFromUrl()` instead of `URLSearchParams.get`.
 */
function ensureFbc() {
  let fbc = getCookie("_fbc");
  if (fbc) return fbc;
  const fbclid = getRawFbclidFromUrl();
  if (!fbclid) return null;
  fbc = `fb.1.${Date.now()}.${fbclid}.${generateAppendix()}`;
  try { document.cookie = `_fbc=${fbc}; path=/; max-age=${60 * 60 * 24 * 90}; SameSite=Lax`; } catch { /* noop */ }
  return fbc;
}

export async function loadPixelIfConsented() {
  if (pixelLoaded) return true;
  if (typeof window === "undefined") return false;
  if (window.fbq && window.fbq.loaded === true) {
    pixelLoaded = true;
    return true;
  }
  const pixelId = await getPixelId();
  if (!pixelId) return false;
  // Pre-warm _fbp / _fbc BEFORE the Pixel inits so both client + server share
  // identical match keys on the very first PageView (otherwise the server-side
  // event lands without fbc and Meta lowers our match quality).
  ensureFbp();
  ensureFbc();
  injectPixelScript();
  try {
    // If a user is already logged in on page load (returning visitor), include
    // `external_id` as Advanced Matching so EVERY subsequent event (PageView
    // included) is enriched with the user identifier.
    const externalId = getCurrentExternalId();
    if (externalId) {
      currentExternalId = externalId;
      window.fbq("init", pixelId, { external_id: externalId });
    } else {
      window.fbq("init", pixelId);
    }
    // Fire PageView with a deterministic event_id so the server-side counterpart
    // (POST /api/events/pageview) deduplicates against this browser event.
    const pageviewEventId = genEventId();
    window.fbq("track", "PageView", {}, { eventID: pageviewEventId });
    pixelLoaded = true;
    // Mirror server-side (best-effort, fire-and-forget). We delay slightly so
    // the Pixel script has time to write `_fbp`/`_fbc` cookies; we then prefer
    // the freshly-written values, falling back to the pre-warmed ones above.
    setTimeout(() => {
      try {
        const fbp = getCookie("_fbp") || ensureFbp();
        const fbc = getCookie("_fbc") || ensureFbc();
        axios.post(`${API}/events/pageview`, {
          event_id: pageviewEventId,
          page_url: typeof window !== "undefined" ? window.location.href : null,
          fbp, fbc,
          external_id: externalId || null,
        }).catch(() => { /* ignore */ });
      } catch { /* ignore */ }
    }, 800);
    reportPixelFired("PageView", pageviewEventId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Apply (or refresh) the `external_id` Advanced Matching on the Pixel.
 * Call this immediately after a user identifies themselves (registration,
 * password setup, login). The Pixel SDK auto-hashes the value.
 *
 * Subsequent `fbq('track', ...)` calls will automatically include the
 * `external_id` user_data parameter — matching what the server sends via CAPI.
 */
export function setPixelUserIdentity(externalId) {
  if (typeof window === "undefined" || !window.fbq) return;
  if (!externalId || externalId === currentExternalId) return;
  try {
    // We need a pixelId to re-init. Use the cached one; if missing, bail out.
    if (!cachedPixelId) return;
    window.fbq("init", cachedPixelId, { external_id: externalId });
    currentExternalId = externalId;
  } catch {
    /* noop */
  }
}

function getCookie(name) {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie.split(";");
  for (const c of cookies) {
    const [k, ...rest] = c.trim().split("=");
    if (k === name) return decodeURIComponent(rest.join("="));
  }
  return null;
}

export function getMetaCookies() {
  // Prefer the cookie value the Pixel wrote; if missing, try to derive it
  // (fbp generated, fbc parsed from `?fbclid=...`). This keeps every server-side
  // event (Lead / CompleteRegistration / Purchase) reporting fbp+fbc to Meta.
  return { fbp: getCookie("_fbp") || ensureFbp(), fbc: getCookie("_fbc") || ensureFbc() };
}

async function reportPixelFired(eventName, eventId) {
  try {
    await axios.post(`${API}/events/pixel-log`, {
      event_id: eventId,
      event_name: eventName,
      fired: true,
    });
  } catch {
    /* ignore */
  }
}

export function trackLead(eventId) {
  if (typeof window === "undefined" || !window.fbq) return;
  try {
    window.fbq("track", "Lead", {}, { eventID: eventId });
    reportPixelFired("Lead", eventId);
  } catch {
    /* noop */
  }
}

export function trackQualifiedLead(eventId, customData = {}) {
  if (typeof window === "undefined" || !window.fbq) return;
  try {
    window.fbq("trackCustom", "QualifiedLead", customData, { eventID: eventId });
    reportPixelFired("QualifiedLead", eventId);
  } catch {
    /* noop */
  }
}

export function trackInitiateCheckout(eventId) {
  if (typeof window === "undefined" || !window.fbq) return;
  try {
    window.fbq("track", "InitiateCheckout", {}, { eventID: eventId });
    reportPixelFired("InitiateCheckout", eventId);
  } catch {
    /* noop */
  }
}

export function trackPurchase(eventId, value, currency = "EUR", lessonKey = null) {
  // Deduped server-side via the same `event_id` from the Stripe webhook.
  if (typeof window === "undefined" || !window.fbq) return;
  try {
    const data = { value, currency };
    if (lessonKey) {
      data.content_name = lessonKey;
      data.content_ids = [lessonKey];
      data.content_type = "product";
    }
    window.fbq("track", "Purchase", data, { eventID: eventId });
    reportPixelFired("Purchase", eventId);
  } catch {
    /* noop */
  }
}

export function trackLessonMilestone(metaEventName, eventId, contentName) {
  // Custom Meta event for lesson milestones (e.g. Lesson1Started, Lesson1Completed)
  // Used to build retargeting audiences. Server-side dedup via `eventId`.
  if (typeof window === "undefined" || !window.fbq) return;
  try {
    window.fbq("trackCustom", metaEventName, {
      content_name: contentName,
      content_ids: [contentName],
      content_type: "lesson_milestone",
    }, { eventID: eventId });
    reportPixelFired(metaEventName, eventId);
  } catch {
    /* noop */
  }
}

export function genEventId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fallthrough */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function isPixelLoaded() {
  return pixelLoaded;
}
